import React from 'react';
import { Network, Shield, CreditCard, Calendar as CalendarIcon, Users, Megaphone, TrendingUp } from 'lucide-react';
import { Card } from '../ui';
import { useTenant } from '../../contexts/TenantContext';
import { useAnalyticsSummary } from '../../hooks/useAnalytics';
import { cn } from '../../lib/utils';

interface StatCardsProps {
  isUniversal: boolean;
  orgCount?: number;
  hasPulse?: boolean;
}

export const StatCards: React.FC<StatCardsProps> = ({ isUniversal, orgCount, hasPulse }) => {
  const { node, getTerm } = useTenant();
  const { data: summary, isLoading } = useAnalyticsSummary(node?.id || '', isUniversal);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-gray-200 border-4 border-black dark:border-white rounded-none animate-pulse dark:bg-slate-900 dark:border-white" />)}
      </div>
    );
  }

  if (isUniversal) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-black border-4 border-black p-6 shadow-[6px_6px_0px_#FFD600]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#FFD600] text-black border-2 border-black flex items-center justify-center">
              <Network size={20} />
            </div>
            <p className="text-xs font-bold text-[#FFD600] uppercase tracking-widest font-mono">My Network</p>
          </div>
          <p className="text-4xl font-black font-display text-white mb-2">{summary?.totalMembers || orgCount}</p>
          <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest font-mono">Connections</p>
        </div>

        <div className="bg-white border-4 border-black p-6 shadow-[6px_6px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[6px_6px_0px_#FFFFFF]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#FFD600] text-black border-2 border-black flex items-center justify-center">
              <Shield size={20} />
            </div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest font-mono">Active Goals</p>
          </div>
          <p className="text-4xl font-black font-display text-black dark:text-white mb-2">{summary?.goalsCompleted || 0}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-widest font-mono">Completed goals</p>
        </div>

        <div className="bg-white border-4 border-black p-6 shadow-[6px_6px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[6px_6px_0px_#FFFFFF]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#0044FF] text-white border-2 border-black flex items-center justify-center">
              <CreditCard size={20} />
            </div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest font-mono">Platform {getTerm('ledger')}</p>
          </div>
          <p className="text-4xl font-black font-display text-black dark:text-white mb-2">${(summary?.totalRevenue || 0).toLocaleString()}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-widest font-mono">Aggregated Network</p>
        </div>

        <div className="bg-white border-4 border-black p-6 shadow-[6px_6px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[6px_6px_0px_#FFFFFF]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#FFD600] text-black border-2 border-black flex items-center justify-center">
              <CalendarIcon size={20} />
            </div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest font-mono">Events</p>
          </div>
          <p className="text-4xl font-black font-display text-black dark:text-white mb-2">{summary?.upcomingEventsCount || 0}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-widest font-mono">Upcoming</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
      <div className="bg-white border-4 border-black p-6 shadow-[6px_6px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[6px_6px_0px_#FFFFFF]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#0044FF] text-white border-2 border-black flex items-center justify-center">
            <Users size={20} />
          </div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest font-mono">
            {getTerm('member')}s
          </p>
        </div>
        <p className="text-4xl font-black font-display text-black dark:text-white mb-2">{(summary?.totalMembers || 0).toLocaleString()}</p>
        <p className={cn(
          "text-[10px] font-bold uppercase tracking-widest font-mono flex items-center gap-1",
          (summary?.memberGrowthPercent || 0) >= 0 ? "text-green-600" : "text-red-600"
        )}>
          <TrendingUp size={12} className={cn((summary?.memberGrowthPercent || 0) < 0 && "rotate-180")} />
          {summary?.memberGrowthPercent}% Growth
        </p>
      </div>

      <div className="bg-white border-4 border-black p-6 shadow-[6px_6px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[6px_6px_0px_#FFFFFF]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#FFD600] text-black border-2 border-black flex items-center justify-center">
            <CreditCard size={20} />
          </div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest font-mono">
            {getTerm('ledger')}
          </p>
        </div>
        <p className="text-4xl font-black font-display text-black dark:text-white mb-2">${(summary?.totalRevenue || 0).toLocaleString()}</p>
        <p className={cn(
          "text-[10px] font-bold uppercase tracking-widest font-mono flex items-center gap-1",
          (summary?.revenueGrowthPercent || 0) >= 0 ? "text-green-600" : "text-red-600"
        )}>
          <TrendingUp size={12} className={cn((summary?.revenueGrowthPercent || 0) < 0 && "rotate-180")} />
          {summary?.revenueGrowthPercent}% Growth
        </p>
      </div>

      <div className="bg-white border-4 border-black p-6 shadow-[6px_6px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[6px_6px_0px_#FFFFFF]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-black text-white border-2 border-black flex items-center justify-center">
            <Megaphone size={20} />
          </div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest font-mono">{getTerm('pulse')}</p>
        </div>
        <p className="text-4xl font-black font-display text-black dark:text-white mb-2">{summary?.activePulsePosts || 0}</p>
        <p className="text-[10px] text-gray-500 uppercase tracking-widest font-mono">Engagement: {summary?.engagementRate}%</p>
      </div>

      <div className="bg-white border-4 border-black p-6 shadow-[6px_6px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[6px_6px_0px_#FFFFFF]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#FFD600] text-black border-2 border-black flex items-center justify-center">
            <CalendarIcon size={20} />
          </div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest font-mono">Events</p>
        </div>
        <p className="text-4xl font-black font-display text-black dark:text-white mb-2">{summary?.upcomingEventsCount || 0}</p>
        <p className="text-[10px] text-gray-500 uppercase tracking-widest font-mono">Upcoming</p>
      </div>
    </div>
  );
};
